import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Personal.css';
import { userAPI } from '../api/user';
import { studyAPI } from '../api/study';
import { exchangeAPI } from '../api/exchange';
import UserInfo from '../components/UserInfo';
import CatalogTree from '../components/CatalogTree';
import '../components/UserInfo.css';

function Personal() {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState('');
  const [score, setScore] = useState(0);
  const [studyTime, setStudyTime] = useState(0);
  const [studyHistory, setStudyHistory] = useState([]);
  const [studyCount, setStudyCount] = useState(0);
  const [mainView, setMainView] = useState('default');
  const [libraryPoems, setLibraryPoems] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [isResizing, setIsResizing] = useState(false);
  const [recordingsMap, setRecordingsMap] = useState({});
  const [playingRecordingId, setPlayingRecordingId] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const audioRefs = useRef({});
  const progressAnimFrames = useRef({});
  const libraryPoemIds = useMemo(
    () => new Set(libraryPoems.map(item => Number(item.poemId || item.id))),
    [libraryPoems]
  );

  // 订单相关状态
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const response = await userAPI.getUserInfo();
        setNickname(response.nickname);
        setAvatar(response.avatar);
        setScore(response.score);
        setStudyTime(response.studyTime);
      } catch (error) {
        console.error('获取用户信息失败', error);
      }
    };

    const getStudyRecords = async () => {
      try {
        const response = await studyAPI.getStudyRecords();
        setStudyHistory(response);
        setStudyCount(response.length);
      } catch (error) {
        console.error('获取学习记录失败', error);
      }
    };

    getUserInfo();
    getStudyRecords();
    fetchUserLibrary();
    fetchUserOrders();

    const loadRecordings = async () => {
      try {
        const data = await studyAPI.getAllRecordings();
        if (Array.isArray(data)) {
          const map = {};
          data.forEach(r => { map[r.poemId] = r; });
          setRecordingsMap(map);
        }
      } catch (e) {
        console.error('加载录音失败', e);
      }
    };
    loadRecordings();

    const handleWindowResize = () => {
      const width = window.innerWidth;
      let newWidth;
      if (width > 768) newWidth = 340;
      else if (width > 480) newWidth = 280;
      else newWidth = '100%';
      setSidebarWidth(newWidth);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const formatStudyTime = (minutes) => {
    if (!minutes && minutes !== 0) return '0分钟';
    const totalMinutes = Number(minutes);
    if (totalMinutes < 60) {
      return `${totalMinutes}分钟`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (mins === 0) {
      return `${hours}小时`;
    }
    return `${hours}小时${mins}分`;
  };

  const handleAvatarChange = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await userAPI.updateAvatar(formData);
      console.log('头像上传成功', response);
      setAvatar(response.avatar);
      window.dispatchEvent(new Event('avatar-updated'));
    } catch (error) {
      console.error('头像上传失败', error);
      alert(error.response?.data?.message || '头像上传失败，请重试');
    }
  };

  const handleCameraCapture = () => {
    // 实现摄像头拍摄逻辑
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          const video = document.createElement('video');
          video.srcObject = stream;
          video.play();
          
          // 显示一个模态框让用户拍摄
          const modal = document.createElement('div');
          modal.style.position = 'fixed';
          modal.style.top = '0';
          modal.style.left = '0';
          modal.style.width = '100%';
          modal.style.height = '100%';
          modal.style.backgroundColor = 'rgba(0,0,0,0.8)';
          modal.style.display = 'flex';
          modal.style.flexDirection = 'column';
          modal.style.alignItems = 'center';
          modal.style.justifyContent = 'center';
          modal.style.zIndex = '1000';
          
          const videoElement = document.createElement('video');
          videoElement.srcObject = stream;
          videoElement.style.width = '80%';
          videoElement.style.maxWidth = '400px';
          videoElement.play();
          
          const captureButton = document.createElement('button');
          captureButton.textContent = '拍摄';
          captureButton.style.marginTop = '20px';
          captureButton.style.padding = '10px 20px';
          captureButton.style.fontSize = '16px';
          captureButton.style.cursor = 'pointer';
          
          const cancelButton = document.createElement('button');
          cancelButton.textContent = '取消';
          cancelButton.style.marginTop = '10px';
          cancelButton.style.padding = '10px 20px';
          cancelButton.style.fontSize = '16px';
          cancelButton.style.cursor = 'pointer';
          
          captureButton.addEventListener('click', async () => {
            const canvas = document.createElement('canvas');
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
            
            canvas.toBlob(async (blob) => {
              try {
                const formData = new FormData();
                formData.append('avatar', blob, 'avatar.jpg');
                
                const response = await userAPI.updateAvatar(formData);
                console.log('头像拍摄成功', response);
                setAvatar(response.avatar);
                window.dispatchEvent(new Event('avatar-updated'));
                
                // 关闭模态框
                document.body.removeChild(modal);
                stream.getTracks().forEach(track => track.stop());
              } catch (error) {
                console.error('头像上传失败', error);
                alert(error.response?.data?.message || '头像上传失败，请重试');
              }
            });
          });
          
          cancelButton.addEventListener('click', () => {
            document.body.removeChild(modal);
            stream.getTracks().forEach(track => track.stop());
          });
          
          modal.appendChild(videoElement);
          modal.appendChild(captureButton);
          modal.appendChild(cancelButton);
          document.body.appendChild(modal);
        })
        .catch(err => {
          console.error('无法访问摄像头', err);
          alert('无法访问摄像头，请检查权限');
        });
    } else {
      alert('您的浏览器不支持摄像头功能');
    }
  };

  const handleRestudy = (poemId) => {
    if (!poemId) return;
    navigate(`/study?poems=${poemId}`);
  };

  const handleCatalogPoemAdded = () => {
    fetchUserLibrary();
  };

  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width > 768) return 'desktop';
    if (width > 480) return 'tablet';
    return 'mobile';
  };

  const handleResizeStart = (e) => {
    if (getDeviceType() === 'mobile') return;
    
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const startWidth = typeof sidebarWidth === 'number' ? sidebarWidth : parseInt(sidebarWidth, 10) || 340;
    
    const handleMove = (moveEvent) => {
      const clientX = moveEvent.type.startsWith('touch') ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const diff = clientX - startX;
      let newWidth = startWidth + diff;

      const device = getDeviceType();
      if (device === 'desktop') newWidth = Math.max(300, Math.min(560, newWidth));
      else if (device === 'tablet') newWidth = Math.max(260, Math.min(440, newWidth));

      setSidebarWidth(newWidth);
    };
    
    const handleUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleUp);
  };

  const fetchUserLibrary = async () => {
    try {
      const response = await studyAPI.getUserLibrary();
      setLibraryPoems(response || []);
    } catch (error) {
      console.error('获取学习库失败', error);
    }
  };

  const handleRemoveFromLibrary = async (poemId) => {
    try {
      await studyAPI.removeFromLibrary(poemId);
      fetchUserLibrary();
    } catch (error) {
      console.error('移除失败', error);
      alert('移除失败，请重试');
    }
  };

  const handlePlayRecording = async (recording) => {
    try {
      if (playingRecordingId === recording.id) {
        const audio = audioRefs.current[recording.id];
        if (audio) {
          audio.pause();
          setPlayingRecordingId(null);
          if (progressAnimFrames.current[recording.id]) {
            cancelAnimationFrame(progressAnimFrames.current[recording.id]);
            delete progressAnimFrames.current[recording.id];
          }
        }
        return;
      }

      const audioBlob = await studyAPI.getRecordingAudio(recording.url || recording.id);
      const url = URL.createObjectURL(audioBlob);

      if (playingRecordingId) {
        const prevAudio = audioRefs.current[playingRecordingId];
        if (prevAudio) {
          prevAudio.pause();
          prevAudio.removeEventListener('timeupdate', prevAudio._timeUpdateHandler);
          prevAudio.src = '';
        }
        if (progressAnimFrames.current[playingRecordingId]) {
          cancelAnimationFrame(progressAnimFrames.current[playingRecordingId]);
          delete progressAnimFrames.current[playingRecordingId];
        }
      }

      setPlayingRecordingId(recording.id);
      setAudioProgress(prev => ({ ...prev, [recording.id]: 0 }));

      setTimeout(() => {
        const audio = audioRefs.current[recording.id];
        if (!audio) return;

        audio.src = url;

        let isPlaying = false;
        const updateProgress = () => {
          if (!isPlaying) return;

          const dur = audio.duration;
          if (dur > 0 && isFinite(dur) && dur !== Infinity && audio.currentTime >= 0) {
            const pct = Math.min(100, Math.max(0, (audio.currentTime / dur) * 100));
            setAudioProgress(prev => ({ ...prev, [recording.id]: pct }));
          }

          if (isPlaying && !audio.paused) {
            progressAnimFrames.current[recording.id] = requestAnimationFrame(updateProgress);
          }
        };

        const onCanPlay = () => {
          console.log('音频可以播放, duration:', audio.duration);
          setAudioProgress(prev => ({ ...prev, [recording.id]: 0 }));
          audio.play().then(() => {
            isPlaying = true;
            updateProgress();
          }).catch(err => {
            console.error('播放失败:', err);
          });
        };

        const onTimeUpdate = () => {
          if (!isPlaying) return;
          const dur = audio.duration;
          if (dur > 0 && isFinite(dur) && dur !== Infinity) {
            const pct = Math.min(100, Math.max(0, (audio.currentTime / dur) * 100));
            setAudioProgress(prev => ({ ...prev, [recording.id]: pct }));
          }
        };

        const onEnded = () => {
          console.log('播放结束');
          isPlaying = false;
          if (progressAnimFrames.current[recording.id]) {
            cancelAnimationFrame(progressAnimFrames.current[recording.id]);
            delete progressAnimFrames.current[recording.id];
          }
          audio.removeEventListener('timeupdate', onTimeUpdate);
          audio.removeEventListener('canplay', onCanPlay);
          setPlayingRecordingId(null);
          setAudioProgress(prev => ({ ...prev, [recording.id]: 100 }));
          setTimeout(() => setAudioProgress(prev => ({ ...prev, [recording.id]: 0 })), 500);
        };

        const onError = (e) => {
          console.error('音频播放错误:', e);
          isPlaying = false;
          if (progressAnimFrames.current[recording.id]) {
            cancelAnimationFrame(progressAnimFrames.current[recording.id]);
            delete progressAnimFrames.current[recording.id];
          }
          audio.removeEventListener('timeupdate', onTimeUpdate);
          audio.removeEventListener('canplay', onCanPlay);
          setPlayingRecordingId(null);
        };

        audio.addEventListener('canplay', onCanPlay, { once: true });
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('ended', onEnded, { once: true });
        audio.addEventListener('error', onError, { once: true });

        audio._timeUpdateHandler = onTimeUpdate;

        audio.load();
      }, 50);
    } catch (error) {
      console.error('播放录音失败:', error);
    }
  };

  // 订单相关函数
  const fetchUserOrders = async () => {
    try {
      const response = await exchangeAPI.getOrders();
      setOrders(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('获取订单列表失败', error);
      setOrders([]);
    }
  };

  const handleViewOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const handleCloseOrderDetail = () => {
    setShowOrderDetail(false);
    setSelectedOrder(null);
  };

  const copyToClipboard = async (text, e) => {
    if (e) e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      alert('✅ 快递单号已复制到剪贴板');
    } catch (err) {
      console.error('复制失败:', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('✅ 快递单号已复制（请手动粘贴）');
      } catch (e) {
        alert('⚠️ 复制失败，请手动复制：' + text);
      }
      document.body.removeChild(textArea);
    }
  };

  const formatOrderDate = (dateStr) => {
    if (!dateStr) return '未知时间';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': '待处理',
      'shipped': '已发货',
      'completed': '已完成'
    };
    return statusMap[status] || status || '未知状态';
  };

  const getStatusClass = (status) => {
    return `order-status-${status}` || '';
  };

  return (
    <div className="personal-container">
      <div className="personal-header">
        <h2>个人中心</h2>
        <UserInfo studyCount={studyCount} currentPage="personal" />
      </div>

      <div className="personal-content">
        <div className="category-sidebar" style={{ width: getDeviceType() === 'mobile' ? '100%' : `${sidebarWidth}px`, position: 'relative' }}>
          <div className="personal-info-section">
            <div className="avatar-section">
              <div className="avatar">
                {avatar ? (
                  <img src={avatar} alt="头像" />
                ) : (
                  <div className="avatar-placeholder">头像</div>
                )}
              </div>
              <div className="avatar-actions">
                <input 
                  type="file" 
                  id="avatar-upload" 
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="avatar-upload" className="btn">选择照片</label>
                <button className="btn" onClick={handleCameraCapture}>拍摄</button>
              </div>
            </div>

            <div className="info-section">
              <div className="info-item">
                <label>昵称</label>
                <input 
                  type="text" 
                  value={nickname} 
                  onChange={(e) => setNickname(e.target.value)}
                />
              </div>
              <div className="info-item">
                <label>元宝积分</label>
                <span>{score}</span>
              </div>
              <div className="info-item">
                <label>学习时长</label>
                <span>{formatStudyTime(studyTime)}</span>
              </div>
            </div>
          </div>

          <div className="sidebar-nav-section">
            <h3>功能菜单</h3>
            <button
              type="button"
              className={`sidebar-nav-btn ${mainView === 'catalog' ? 'active' : ''}`}
              onClick={() => setMainView('catalog')}
            >
              📚 教材选择
            </button>
            <button
              type="button"
              className={`sidebar-nav-btn ${mainView === 'default' ? 'active' : ''}`}
              onClick={() => setMainView('default')}
            >
              📜 学习历史
            </button>
          </div>

          {getDeviceType() !== 'mobile' && (
            <div className="sidebar-resize-handle" onMouseDown={handleResizeStart} onTouchStart={handleResizeStart} />
          )}
        </div>

        <div className="personal-main">
          {mainView === 'catalog' ? (
            <CatalogTree
              libraryPoemIds={libraryPoemIds}
              onAdded={handleCatalogPoemAdded}
            />
          ) : (
            <>
          <div className="study-history">
            <h3>学习历史</h3>
            <div className="history-list">
              {studyHistory.map((item) => (
                <div key={item.id} className="history-item">
                  <div className="history-content">
                    <div>
                      <h4>{item.title || item.poem?.title || '未知古诗'}</h4>
                      <span className="history-meta">
                        {item.author || item.poem?.author || '未知作者'}
                        {item.dynasty || item.poem?.dynasty ? ` · ${item.dynasty || item.poem.dynasty}` : ''}
                      </span>
                    </div>
                    <div className="history-actions">
                      <span className="history-date">
                        {item.studyTime ? new Date(item.studyTime).toLocaleDateString('zh-CN') :
                         item.createdAt ? new Date(item.createdAt).toLocaleDateString('zh-CN') : '未知时间'}
                      </span>
                      <button
                        type="button"
                        className="restudy-btn"
                        onClick={() => handleRestudy(item.poemId)}
                        title="再次学习"
                      >
                        📖 再学一次
                      </button>
                      {recordingsMap[item.poemId] && (
                        <>
                          <button
                            className="play-recording-btn"
                            onClick={(e) => { e.stopPropagation(); handlePlayRecording(recordingsMap[item.poemId]); }}
                            title="播放朗读录音"
                          >
                            🔊 {playingRecordingId === recordingsMap[item.poemId].id ? '暂停' : '回放'}
                          </button>
                          {playingRecordingId === recordingsMap[item.poemId]?.id && (
                            <div className="inline-progress-bar">
                              <div className="inline-progress-fill" style={{ width: `${audioProgress[recordingsMap[item.poemId].id] || 0}%` }} />
                              <audio ref={(el) => { audioRefs.current[recordingsMap[item.poemId].id] = el; }} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="main-summary-grid">
            <div className="library-preview-section">
              <h3>📚 我的学习库</h3>
              {libraryPoems.length > 0 ? (
                <div className="library-poem-list">
                  {libraryPoems.map((item) => (
                    <div key={item.id} className="library-poem-item">
                      <div className="library-poem-info">
                        <span className="poem-title">{item.title || '未知古诗'}</span>
                        <span className="poem-author">{item.author || '未知作者'}{item.dynasty ? ` · ${item.dynasty}` : ''}</span>
                      </div>
                      <button
                        className="btn remove-btn"
                        onClick={() => handleRemoveFromLibrary(item.poemId)}
                      >
                        移除
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="library-empty">暂无收藏古诗</p>
              )}
            </div>

            <div className="order-section-sidebar">
              <h3>📦 我的订单</h3>
              {orders.length === 0 ? (
                <div className="empty-orders-sidebar">
                  <p>暂无兑换记录~</p>
                </div>
              ) : (
                <div className="orders-list-sidebar">
                  {orders.slice(0, 5).map(order => (
                    <div
                      key={order.id}
                      className={`order-card-small ${getStatusClass(order.status)}`}
                      onClick={() => handleViewOrderDetail(order)}
                    >
                      <span className="order-name">{order.productName || '商品'}</span>
                      <span className="order-status-text">{getStatusLabel(order.status)}</span>
                    </div>
                  ))}
                  {orders.length > 5 && (
                    <p className="view-all-orders">共 {orders.length} 条订单</p>
                  )}
                </div>
              )}
            </div>
          </div>
            </>
          )}

          {/* 订单详情弹窗 */}
          {showOrderDetail && selectedOrder && (
            <div className="order-detail-overlay" onClick={handleCloseOrderDetail}>
              <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
                <button className="order-close-btn" onClick={handleCloseOrderDetail}>✕</button>

                <h2 className="detail-title">订单详情 #{selectedOrder.id}</h2>

                <div className="detail-section">
                  <h3 className="section-subtitle">📦 商品信息</h3>
                  <div className="product-detail-content">
                    <img
                      src={selectedOrder.productImage || selectedOrder.imageUrl || '/placeholder.png'}
                      alt={selectedOrder.productName || '商品'}
                      className="detail-product-image"
                      onError={(e) => { e.target.src = '/placeholder.png'; }}
                    />
                    <div className="product-text-info">
                      <p className="detail-name">{selectedOrder.productName || '商品名称'}</p>
                      <p className="detail-desc">{selectedOrder.productDescription || selectedOrder.description || '暂无描述'}</p>
                      <p className="detail-quantity">数量: {selectedOrder.quantity || 1}</p>
                      <p className="detail-price">单价: {selectedOrder.price || selectedOrder.totalPrice} 元宝</p>
                      <p className="detail-total-price">总计: <strong>{selectedOrder.totalPrice} 元宝</strong></p>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3 className="section-subtitle">📍 收货信息</h3>
                  <p className="address-text">{selectedOrder.address || '暂无地址信息'}</p>
                </div>

                {(selectedOrder.deliveryCompany || selectedOrder.deliveryNumber) && (
                  <div className="detail-section delivery-info-section">
                    <h3 className="section-subtitle">🚚 快递信息</h3>
                    <div className="delivery-details">
                      <div className="delivery-item">
                        <span className="delivery-label">快递公司：</span>
                        <span className="delivery-value">{selectedOrder.deliveryCompany}</span>
                      </div>
                      <div className="delivery-item delivery-number-item">
                        <span className="delivery-label">快递单号：</span>
                        <span className="delivery-value number-value">{selectedOrder.deliveryNumber}</span>
                        <button
                          className="copy-number-btn"
                          onClick={(e) => copyToClipboard(selectedOrder.deliveryNumber, e)}
                          title="复制单号"
                        >
                          📋 复制
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <h3 className="section-subtitle">📋 订单信息</h3>
                  <div className="order-meta-grid">
                    <div className="meta-item">
                      <span className="meta-label">下单时间：</span>
                      <span className="meta-value">{formatOrderDate(selectedOrder.orderTime)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">订单状态：</span>
                      <span className={`status-tag ${getStatusClass(selectedOrder.status)}`}>
                        {getStatusLabel(selectedOrder.status)}
                      </span>
                    </div>
                    {selectedOrder.shippedTime && (
                      <div className="meta-item">
                        <span className="meta-label">发货时间：</span>
                        <span className="meta-value">{formatOrderDate(selectedOrder.shippedTime)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Personal;